import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, X, ArrowUp, ArrowDown, UserList } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { StatusPill, ROSTER_STATUS_META } from "./StatusPill";

const STATUS_META = ROSTER_STATUS_META;
const STATUS_ORDER = ["active", "leave", "inactive", "resigned", "terminated", "never_worked", "declined", "applicant"];
const COMPLIANCE_DOT = {
  green: "bg-emerald-500", yellow: "bg-[#B7791F]", red: "bg-[#C0392B]", gray: "bg-stone-300",
};
const COMPLIANCE_LABEL = { green: "Current", yellow: "Due soon", red: "Needs attention", gray: "Not tracked" };

function relDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const abs = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (days <= 0) return `${abs} (today)`;
  if (days === 1) return `${abs} (yesterday)`;
  if (days < 30) return `${abs} (${days}d ago)`;
  if (days < 365) return `${abs} (${Math.floor(days / 30)}mo ago)`;
  return `${abs} (${(days / 365).toFixed(1)}y ago)`;
}

export function AdminRoster() {
  const [staff, setStaff] = useState(null);
  const [search, setSearch] = useState("");
  const [statuses, setStatuses] = useState([]); // empty = all
  const [workedSince, setWorkedSince] = useState("2024-09-15");
  const [onlyWorked, setOnlyWorked] = useState(true);
  const [sortKey, setSortKey] = useState("status");
  const [sortDir, setSortDir] = useState(1);

  useEffect(() => {
    api.get("/roster").then((r) => setStaff(r.data.staff || []))
      .catch((e) => { toast.error(formatApiError(e)); setStaff([]); });
  }, []);

  const toggleStatus = (s) =>
    setStatuses((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const rows = useMemo(() => {
    if (!staff) return [];
    let f = staff;
    if (onlyWorked && workedSince) f = f.filter((r) => r.last_worked && r.last_worked >= workedSince);
    if (statuses.length) f = f.filter((r) => statuses.includes(r.status));
    const q = search.trim().toLowerCase();
    if (q) f = f.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q) || (r.employee_id || "").includes(q));
    const keyFn = {
      status: (r) => STATUS_ORDER.indexOf(r.status) === -1 ? 99 : STATUS_ORDER.indexOf(r.status),
      start_date: (r) => r.start_date || "9999",
      last_worked: (r) => r.last_worked || "0000",
      first_worked: (r) => r.first_worked || "9999",
      name: (r) => (r.name || "").toLowerCase(),
      last_name: (r) => {
        const parts = (r.name || "").trim().toLowerCase().split(/\s+/);
        return (parts[parts.length - 1] || "") + " " + (parts[0] || "");
      },
    }[sortKey] || ((r) => r.name);
    return [...f].sort((a, b) => {
      const ka = keyFn(a), kb = keyFn(b);
      let cmp = ka < kb ? -1 : ka > kb ? 1 : 0;
      if (cmp === 0 && sortKey === "status") {
        const ha = a.start_date || "9999", hb = b.start_date || "9999";
        cmp = ha < hb ? -1 : ha > hb ? 1 : 0; // default view: actives first, then by hire date
      }
      if (cmp === 0) cmp = (a.name || "").localeCompare(b.name || "");
      return cmp * sortDir;
    });
  }, [staff, search, statuses, workedSince, onlyWorked, sortKey, sortDir]);

  const headerBtn = (label, key, tid) => (
    <button onClick={() => { if (sortKey === key) setSortDir((d) => -d); else { setSortKey(key); setSortDir(1); } }}
      className="inline-flex items-center gap-1 font-medium uppercase tracking-wide hover:text-stone-800 transition-colors"
      data-testid={tid}>
      {label}
      {sortKey === key && (sortDir === 1 ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
    </button>
  );

  return (
    <section className="rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="roster-section">
      <div className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-6 py-4">
        <div className="flex items-center gap-2">
          <UserList size={20} className="text-[#4A235A]" />
          <h2 className="font-heading text-2xl tracking-tight text-[#4A235A]">The roster</h2>
        </div>
        <span className="text-xs text-stone-400" data-testid="roster-count">
          {staff === null ? "loading…" : `${rows.length} of ${staff.length} people`}
        </span>
        <div className="relative ml-auto">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, or ID…"
            className="w-64 rounded-lg border border-stone-200 bg-stone-50 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A235A]/50"
            data-testid="roster-search-input" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-6 py-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Status</span>
        {STATUS_ORDER.filter((s) => (staff || []).some((r) => r.status === s)).map((s) => (
          <button key={s} onClick={() => toggleStatus(s)}
            className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors duration-200 ${statuses.includes(s) ? STATUS_META[s].cls + " ring-1 ring-current" : "border-stone-200 text-stone-400 hover:text-stone-600"}`}
            data-testid={`roster-status-filter-${s}`}>
            {STATUS_META[s].label}
          </button>
        ))}
        <span className="ml-3 text-[10px] font-semibold uppercase tracking-wider text-stone-400">Worked since</span>
        <input type="date" value={workedSince} onChange={(e) => setWorkedSince(e.target.value)}
          className="rounded-md border border-stone-200 bg-white px-2 py-0.5 text-xs text-stone-600"
          data-testid="roster-date-filter" />
        <label className="flex items-center gap-1.5 text-xs text-stone-500">
          <input type="checkbox" checked={onlyWorked} onChange={(e) => setOnlyWorked(e.target.checked)}
            className="accent-[#4A235A]" data-testid="roster-only-worked" />
          worked at least a day
        </label>
        <label className="flex items-center gap-1.5 text-xs text-stone-500">
          Sort by
          <select value={sortKey}
            onChange={(e) => { setSortKey(e.target.value); setSortDir(e.target.value === "last_worked" ? -1 : 1); }}
            className="rounded-md border border-stone-200 bg-white px-2 py-0.5 text-xs text-stone-600"
            data-testid="roster-sort-select">
            <option value="status">Active, by hire date</option>
            <option value="name">First name A–Z</option>
            <option value="last_name">Last name A–Z</option>
            <option value="start_date">Hire date</option>
            <option value="first_worked">First day worked</option>
            <option value="last_worked">Last day worked</option>
          </select>
        </label>
        {(statuses.length > 0 || search || !onlyWorked || workedSince !== "2024-01-01") && (
          <button onClick={() => { setStatuses([]); setSearch(""); setOnlyWorked(true); setWorkedSince("2024-09-15"); }}
            className="ml-auto text-xs font-medium text-[#4A235A] hover:underline" data-testid="roster-clear-filters">
            Clear filters
          </button>
        )}
      </div>

      <div className="max-h-[540px] overflow-y-auto">
        <table className="w-full text-left text-sm" data-testid="roster-data-table">
          <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_#e7e5e4]">
            <tr className="text-xs text-stone-500">
              <th className="px-6 py-3">{headerBtn("Employee", "name", "roster-sort-name")}</th>
              <th className="px-3 py-3 font-medium">ID</th>
              <th className="px-3 py-3">{headerBtn("Status", "status", "roster-sort-status")}</th>
              <th className="px-3 py-3">{headerBtn("Start date", "start_date", "roster-sort-start")}</th>
              <th className="px-3 py-3">{headerBtn("Last day worked", "last_worked", "roster-sort-worked")}</th>
              <th className="px-6 py-3 font-medium">Compliance</th>
            </tr>
          </thead>
          <tbody>
            {staff === null && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-stone-400">Loading the roster…</td></tr>
            )}
            {staff !== null && rows.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-stone-400" data-testid="roster-empty">Nobody matches these filters.</td></tr>
            )}
            {rows.slice(0, 300).map((r, i) => (
              <tr key={r.id} className="border-b border-stone-100 transition-colors duration-200 last:border-0 hover:bg-stone-50"
                data-testid={`roster-table-row`}>
                <td className="px-6 py-3">
                  <Link to={`/team/${r.id}`} className="font-medium text-stone-900 hover:text-[#4A235A] hover:underline"
                    data-testid={`roster-name-${i}`}>{r.name}</Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                    {r.phone && (
                      <>
                        <a href={`tel:${r.phone}`} className="font-medium text-stone-500 hover:text-[#4A235A] hover:underline" data-testid={`roster-phone-${i}`}>{r.phone}</a>
                        <a href={`sms:${r.phone}`} className="font-medium text-stone-500 hover:text-[#4A235A] hover:underline" data-testid={`roster-text-${i}`}>Text</a>
                      </>
                    )}
                    {r.email ? (
                      <a href={`mailto:${r.email}`} className="text-stone-500 hover:text-[#4A235A] hover:underline" data-testid={`roster-email-${i}`}>{r.email}</a>
                    ) : (
                      <span className="text-stone-400" data-testid={`roster-email-${i}`}>no email on file</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 tabular-nums text-stone-500">{r.employee_id || "—"}</td>
                <td className="px-3 py-3">
                  <StatusPill uid={r.id} status={r.status} testid={`roster-status-${i}`}
                    onChanged={(s) => setStaff((cur) => (cur || []).map((x) => (x.id === r.id ? { ...x, status: s } : x)))} />
                  {!r.first_worked && !r.last_worked && (
                    <span className="mt-1 block w-fit rounded-full border border-stone-300 bg-stone-50 px-2 py-px text-[10px] font-semibold text-stone-500"
                      title="No shifts on record in HHAeXchange — hired but never worked"
                      data-testid={`roster-neverworked-${i}`}>never worked a day</span>
                  )}
                </td>
                <td className="px-3 py-3 text-stone-600">{r.start_date || "—"}</td>
                <td className="px-3 py-3 text-stone-600">{relDate(r.last_worked)}</td>
                <td className="px-6 py-3">
                  {r.compliance ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-stone-600" data-testid={`roster-compliance-${i}`}>
                      <span className={`h-2 w-2 rounded-full ${COMPLIANCE_DOT[r.compliance] || COMPLIANCE_DOT.gray}`} />
                      {COMPLIANCE_LABEL[r.compliance] || "—"}
                    </span>
                  ) : <span className="text-xs text-stone-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 300 && <p className="border-t border-stone-100 px-6 py-2 text-xs text-stone-400">Showing first 300 — narrow with filters.</p>}
      </div>
    </section>
  );
}
